import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Chats from '../feedbackdetail/Chats'
import AiReasoningReport from '../feedbackdetail/AiReasoningReport'
import Typing from '../feedbackdetail/Typing'
import ChatAttachmentPreview from '../feedbackdetail/ChatAttachmentPreview'
import { buildAttachment, showAttachmentError, validateAttachment } from '../feedbackdetail/chatAttachmentUtils'
import ChatSectionButtons from './ChatSectionButtons'
import { Toaster } from 'react-hot-toast'
import { notifyError, notifySuccess } from '../ToastMessages'
import Alert from '../Alert'
import {
    aiRequestNotAllowed,
    ajaxNewChatVC,
    ajaxTyping,
    getRequestedVia,
    noAiResponseWarning,
    resetFeedbackPrompt,
    secondsToDhms,
    setLoading,
    setTimerInterval,
    warnNoChat,
    warnUserDidntRequestedChat
} from '../redux/FeedbackRedux'
import AiButton from './AiButton'

import { TypeAnimation } from 'react-type-animation'
import { getAutomationIndicatorConfig as getAutomationIndicatorConfigRaw } from './automationIndicatorConfig'

// used for automated ai response
function CountdownTimer({ startSeconds = 100, onComplete }) {
    const [secondsLeft, setSecondsLeft] = useState(startSeconds);
    const hasCompleted = useRef(false);

    useEffect(() => {
        const interval = setInterval(() => {
            setSecondsLeft((prev) => {
                const next = prev - 1;
                if (next <= 0) {
                    clearInterval(interval);
                    return 0;
                }
                return next;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (
            secondsLeft === 0 &&
            typeof onComplete === "function" &&
            !hasCompleted.current
        ) {
            hasCompleted.current = true;
            onComplete();
        }
    }, [secondsLeft, onComplete]);

    const safeSeconds = typeof secondsLeft === 'number' ? secondsLeft : 0;
    const days = Math.floor(safeSeconds / 86400);
    const hours = Math.floor((safeSeconds % 86400) / 3600);
    const mins = Math.floor((safeSeconds % 3600) / 60);
    const secs = safeSeconds % 60;

    return (
        <span>
          {Number.isFinite(secs) ? (
              <>
                  {days > 0 ? `${days}d ` : ""}
                  {hours > 0 || days > 0 ? `${hours}h ` : ""}
                  {mins > 0 || hours > 0 || days > 0 ? `${mins}m ` : ""}
                  {secs}s
              </>
          ) : (
              "–"
          )}
        </span>
    );
}

//
function getSecondsUntil(isoDateString) {
    const scheduledTime = new Date(isoDateString).getTime();
    const now = Date.now();
    const diff = Math.floor((scheduledTime - now) / 1000);
    return Math.max(diff, 0);
}

/** Applies translation to raw config from automationIndicatorConfig. */
function getAutomationIndicatorConfig(automationType, isStopped, stopReason) {
    const raw = getAutomationIndicatorConfigRaw(automationType, isStopped, stopReason);
    return {
        label: raw.label ? __(raw.label) : raw.label,
        tooltip: raw.tooltip ? __(raw.tooltip) : raw.tooltip
    };
}

// Unique per instance: FeedbackDetail mounts ChatSection 3x (desktop/mobile/print),
// so a shared input id would make the label target the wrong (hidden) instance.
let chatAttachmentInputSeq = 0;

class ChatSection extends React.Component {

    constructor(props) {
        super(props);

        const id = this.props.feedback_id;
        this.attachmentInputId = `feedback_attachment_${++chatAttachmentInputSeq}`;
        const storage_typing_messages = localStorage.getItem("typing_messages");
        const typing_message = storage_typing_messages === null ? [] : JSON.parse(storage_typing_messages);
        const saved_message = typing_message.filter(message => message.id === this.props.feedback_id )

        this.state = {
            value: saved_message.length > 0 ? saved_message[0].message : '',
            initial_feedback: id,
            cleared: false,
            last_typing: Math.floor(Date.now() / 1000),
            sent_first: false,
            timeSinceFeedBackStarted: this.handleTimeSinceFeedBackStarted(),
            typing_message: typing_message,
            seconds: 15,
            isAiPreviewVisible: null,
            promptOutput: "",
            hideAiRecommendationBox: false,
            aiSettingsAutomationType: "",
            ai_chat_stopped_at: "",
            ai_chat_stop_reason: null,
            warnHumanIntervention: false,
            humanIntervention: false,
            ai_chat_status: "",
            cancelHidden: false,
            countdownStart: null,
            scheduledAt: null,
            activePromptId: null,
            attachments: [],
        };

        this.handleAttachmentChange = this.handleAttachmentChange.bind(this);
        this.handleRemoveAttachment = this.handleRemoveAttachment.bind(this);
        this.resetAttachments = this.resetAttachments.bind(this);
        this.handleTick = this.handleTick.bind(this)
        this.handleClickAiButton = this.handleClickAiButton.bind(this)
        this.clearTimer = this.clearTimer.bind(this);
        this.componentDidUpdate = this.componentDidUpdate.bind(this);
        this.calcTextareaHeight = this.calcTextareaHeight.bind(this);
        this.automationIndicatorTriggerRef = null;
        this._lastAutomationTooltipKey = null;
    }

    /** Initialize Bootstrap tooltip on the automation indicator. Only runs when indicator is visible and when automation state (tooltip content) actually changed, so we don't dispose/recreate on every update and break hover. */
    initAutomationIndicatorTooltip() {
        const hasConversationAutomation =
            this.state.aiSettingsAutomationType === 'full'
            || this.state.aiSettingsAutomationType === 'semi';

        if (!this.automationIndicatorTriggerRef || !hasConversationAutomation) {
            this._lastAutomationTooltipKey = null;
            return;
        }

        const isStopped = !!(this.state.ai_chat_stopped_at || this.props?.feedback?.ai_chat_stopped_at);
        const stopReason = this.state.ai_chat_stop_reason ?? this.props?.feedback?.ai_chat_stop_reason ?? null;
        const key = `${this.state.aiSettingsAutomationType}-${isStopped}-${stopReason}`;
        if (this._lastAutomationTooltipKey === key) {
            const $el = jQuery(this.automationIndicatorTriggerRef);
            if ($el.data('bs.tooltip')) return;
        }
        this._lastAutomationTooltipKey = key;
        const $el = jQuery(this.automationIndicatorTriggerRef);
        try {
            if ($el.data('bs.tooltip')) $el.tooltip('dispose');
        } catch (e) { /* ignore */ }
        const config = getAutomationIndicatorConfig(this.state.aiSettingsAutomationType, isStopped, stopReason);
        $el.tooltip({
            title: config.tooltip,
            html: true,
            placement: 'bottom',
            container: 'body',
            boundary: 'window',
            delay: { show: 200, hide: 150 }
        });
    }

    calcTextareaHeight() {
        const textarea = document.querySelectorAll(".chat-dialog-textarea");

        textarea.forEach(textarea => {
            textarea.style.height = "";
            textarea.style.height = textarea.scrollHeight + "px";
        });
    }

    pasteMessage(data, promptId = null) {
        this.setState({ isAiPreviewVisible: false });

        this.handleChatType();

        this.saveToTypingMessage(data);
        this.setState(
            {
                value: data,
                activePromptId: promptId,
                humanIntervention: true,
                hideAiRecommendationBox: true
            }, () => {
                this.calcTextareaHeight();
            }
        );

        this.setState({ warnHumanIntervention: true });

        // RTF-4582: When pasting an AI recommendation in semi/fully-automated mode,
        // also cancel the chat automation timer for this conversation so the
        // pending AI message doesn't auto-send while the user edits the pasted text.
        const automationType = this.state.aiSettingsAutomationType;
        const alreadyStopped = !!(this.state.ai_chat_stopped_at || this.props?.feedback?.ai_chat_stopped_at);
        if ((automationType === 'full' || automationType === 'semi') && !alreadyStopped) {
            this.stopAiChatAutomation({ silent: true });
        }
    }

    handlePreCanned(event) {
        event.preventDefault();
        if (!this.canSendChat()) return;

        this.handleChatType();

        if (!(this.props.feedback.is_include_contact && this.props.feedback.user.phone)) {
            warnNoChat(this.props.feedback.is_include_contact, this.props.dispatch);
            return;
        }
        var data = jQuery(event.target).attr('data-text');
        this.saveToTypingMessage(data);
        this.setState(
            { value: data }, () => {
                jQuery("#chat-textarea").trigger("input");
                this.calcTextareaHeight();
            }
        );
    }

    handleClickAiButton(event) {
        if (this.props.feedback.chat_status !== 'Chat Not Started') {
            aiRequestNotAllowed(this.props.dispatch);
            return;
        }
        this.props.dispatch(setLoading({
            loading: true,
            location: 'aiButton',
            loadingData: {'feedbackId': this.props.feedback.feedback_id}
        }));

        fetch('/feedback/' + this.props.feedback.feedback_id + '/ai/last-prompt', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
        .then(res => res.json())
        .then(response => {
            if (!response.errors) {
                const data = response.promptOutput;
                this.saveToTypingMessage(data);
                this.setState(
                    {value: data, seconds: 15, activePromptId: response.id}, () => {
                        let element = document.getElementById('chat-textarea');
                        if (element) {
                            element.focus();
                            this.calcTextareaHeight();
                        }
                    }
                );
                clearInterval(this.interval)
                this.props.dispatch(setLoading({loading: false, location: 'aiButton', data: null}));
            }
        })
        .catch((err) => {
            this.props.dispatch(setLoading({loading: false, location: 'aiButton'}));
        });
    }

    handleTick() {
        // start timer after button is clicked
        this.handleClickAiButton()
        this.interval = setInterval(() => {
            if (this.state.seconds !== 0) {
                this.setState(prevState => ({
                    seconds: prevState.seconds - 1
                }));
            } else {
                this.setState({ seconds: 15 })
                noAiResponseWarning(this.props.dispatch)
                this.props.dispatch(setLoading({loading: false, location: undefined, data: undefined}));
                clearInterval(this.interval);
            }
        }, 1000);
    }

    saveToTypingMessage(message) {
        if (message === "") return this.removeTypingMessage();
        if (this.state.typing_message.findIndex((value) => {
            if (value.id === this.state.initial_feedback) {
                return true;
            } else {
                return false;
            }
        }) === -1) {
            this.setState({
                typing_message: [...this.state.typing_message, {id: this.state.initial_feedback, message: message}]
            }, () => {
                localStorage.setItem("typing_messages", JSON.stringify(this.state.typing_message));
            })
        } else {
            this.setState({
                typing_message: this.state.typing_message.map((value) => {
                    if (value.id === this.state.initial_feedback)
                        return { id: this.state.initial_feedback, message: message };
                    else {
                        return value;
                    }
                })
            }, () => {
                localStorage.setItem("typing_messages", JSON.stringify(this.state.typing_message));
            })
        }
    }

    removeTypingMessage() {
        this.setState({
            typing_message: this.state.typing_message.filter((value) => {
                return value.id !== this.state.initial_feedback;
            })
        }, () => {
            localStorage.setItem("typing_messages", JSON.stringify(this.state.typing_message));
        })
    }

    handleReview(event) {
        event.preventDefault();
        if (!this.canSendChat()) return;
        if (!(this.props.feedback.is_include_contact && this.props.feedback.user.phone)) {
            warnNoChat(this.props.feedback.is_include_contact, this.props.dispatch);
            return;
        }
        var data = jQuery(event.target).attr('data-text');
        this.setState(
            { value: data },
            () => {
                jQuery("#chat-textarea").trigger("input");
                this.calcTextareaHeight();
            }
        );
    }

    handleReviewOnly(event) {
        event.preventDefault();
        if (!this.canSendChat()) return;

        if (!(this.props.feedback.is_include_contact && this.props.feedback.user.phone)) {
            warnNoChat(this.props.feedback.is_include_contact, this.props.dispatch);
            return;
        }

        const dataText = jQuery(event.target).attr('data-text');
        jQuery(".chat-dialog-textarea").each((i, elem) => {
            let textareaVal = jQuery(elem).val() + " " + dataText;
            textareaVal = textareaVal.replace(/\s+/g, ' ').trim();

            this.setState(
                { value: textareaVal },
                () => {
                    jQuery(elem).val(textareaVal).trigger("input");
                    this.calcTextareaHeight();
                }
            );
        });
    }

    handleTimeSinceFeedBackStarted() {
        if (!this.props.feedback?.timeStart) {
            return "";
        }
        const startDate = new Date(this.props.feedback.timeStart);
        const endDate = new Date();
        const seconds = (+endDate - +startDate) / 1000;

        return secondsToDhms(seconds, false, true);
    }

    // RTF-4374: the authenticated user's "may send a chat" permission, read from
    // the serialized Auth::user() exposed on the feedback detail page.
    canSendChat() {
        return window.currentUser?.can_send_chat !== false;
    }

    handleChange(event) {
        this.setState(
            { value: event.target.value }
        );
        this.saveToTypingMessage(event.target.value);
        if (this.state.cleared !== false) {
            this.setState({
                cleared: false,
            });
        }
        const now = Math.floor(Date.now() / 1000);
        if (this.state.sent_first === true) {
            if (now > (this.state.last_typing + 2)) {
                this.props.dispatch(ajaxTyping(this.props.feedback.feedback_id, true));
                this.setState(
                    { last_typing: now }
                );
            }
        } else {
            this.props.dispatch(ajaxTyping(this.props.feedback.feedback_id, true));
            this.setState(
                { last_typing: now, sent_first: true }
            );
        }

        event.target.style.height = ""; // Reset height
        event.target.style.height = event.target.scrollHeight + "px"; // Adjust height dynamically
    }

    // window.chatAttachments is the buffer ajaxNewChatVC sends from.
    handleAttachmentChange(event) {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) {
            return;
        }

        const accepted = [];
        files.forEach((file) => {
            const error = validateAttachment(file);
            if (error) {
                showAttachmentError(error);
                return;
            }
            accepted.push(buildAttachment(file));
        });

        // Reset so re-selecting the same file fires change again.
        event.target.value = '';

        // Skip files already attached.
        window.chatAttachments = window.chatAttachments || [];
        const existingKeys = new Set([
            ...this.state.attachments.map((attachment) => attachment.key),
            ...window.chatAttachments.map((item) => item.key)
        ]);
        const unique = accepted.filter((attachment) => {
            if (existingKeys.has(attachment.key)) {
                if (attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                }
                return false;
            }
            existingKeys.add(attachment.key);
            return true;
        });

        if (unique.length === 0) {
            return;
        }

        unique.forEach((attachment) => {
            window.chatAttachments.push({ key: attachment.key, obj: attachment.file });
        });

        this.setState((prevState) => ({
            attachments: [...prevState.attachments, ...unique]
        }));
    }

    handleRemoveAttachment(key) {
        this.setState((prevState) => ({
            attachments: prevState.attachments.filter((attachment) => {
                if (attachment.key === key && attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                }
                return attachment.key !== key;
            })
        }));

        window.chatAttachments = (window.chatAttachments || []).filter((item) => item.key !== key);
    }

    // No setState — safe to call from componentWillUnmount.
    revokeAttachmentUrls() {
        this.state.attachments.forEach((attachment) => {
            if (attachment.previewUrl) {
                URL.revokeObjectURL(attachment.previewUrl);
            }
        });
    }

    resetAttachments() {
        this.revokeAttachmentUrls();
        window.chatAttachments = [];
        this.setState({ attachments: [] });
    }

    handleSend() {
        if (!this.canSendChat()) return;
        if (!(this.props.feedback.is_include_contact && this.props.feedback.user.phone)) {
            warnNoChat(this.props.feedback.is_include_contact, this.props.dispatch);
            return;
        }
        const hasContent = this.state.value.trim() !== '' || window.chatAttachments.length > 0;
        if (this.props.loading || !hasContent) {
            return;
        }
        this.props.dispatch(ajaxTyping(this.props.feedback.feedback_id, false));
        this.props.dispatch(ajaxNewChatVC(this.state.value, this.props.feedback.feedback_id, this.state.activePromptId));
        this.removeTypingMessage();
        this.resetAttachments();
        this.setState({
            value: '',
            activePromptId: null,
            humanIntervention: true,
            ai_chat_stopped_at: new Date().toISOString(),
            ai_chat_stop_reason: 'human_intervention',
            hideAiRecommendationBox: true
        });

        if (this.props.feedbackPrompt)
            this.props.dispatch(resetFeedbackPrompt())
    }

    handleTextClick(event) {
        if (!this.canSendChat()) return;
        if (!(this.props.feedback.is_include_contact && this.props.feedback.user.phone)) {
            warnNoChat(this.props.feedback.is_include_contact, this.props.dispatch);
            return;
        }
        if (event.target.value == "") {
            this.removeTypingMessage();
            this.setState(
                { value: '' }
            );
        }
    }

    handleGpsRequest(event) {
        event.preventDefault();
        if (!this.canSendChat()) return;

        if (!(this.props.feedback.is_include_contact && this.props.feedback.user.phone)) {
            warnNoChat(this.props.feedback.is_include_contact, this.props.dispatch);
            return;
        }

        const data = event.target.getAttribute('data-text');
        this.saveToTypingMessage(data);
        this.setState(
            { value: data },
            () => {
                const chatTextarea = document.getElementById("chat-textarea");
                if (chatTextarea) {
                    const inputEvent = new Event("input", { bubbles: true, cancelable: true });
                    chatTextarea.dispatchEvent(inputEvent);
                }
            }
        );
    }

    handleChatType() {
        if (!this.props.feedback.is_text && !this.props.feedback.is_whatsapp && this.props.feedback.user.phone) {
            if (!this.props.chatAnyway) {
                warnUserDidntRequestedChat(this.props.dispatch);
            }
        }
    }

    clearTimer() {
        clearInterval(this.interval)
        if (this.props.feedbackPrompt)
            this.setState({ value: this.props.feedbackPrompt, seconds: 15, activePromptId: this.props.promptObj?.id || null })
    }

    handleCancelAutomation() {
        this.stopAiChatAutomation({ silent: false });
    }

    /**
     * Calls the stop endpoint for AI chat automation on the current feedback.
     * Used by both the explicit "Cancel" button (with toast) and by
     * pasteMessage() (silent) per RTF-4582.
     */
    stopAiChatAutomation({ silent = false } = {}) {
        const feedbackId = this.props.feedback?.feedback_id;

        if (!feedbackId) return;

        $.ajax({
            url: `/feedback/${feedbackId}/chat-ai-autoresponse/stop`,
            method: "PATCH",
            success: () => {
                this.setState({
                    ai_chat_stopped_at: new Date().toISOString(),
                    ai_chat_stop_reason: 'manual_stop',
                    hideAiRecommendationBox: true
                });

                if (!silent) {
                    notifySuccess(__('Automation stopped successfully!'));
                }
            },
            error: (err) => {
                console.error("Failed to stop AI auto response", err);
            },
        });
    }

    handleSendNow() {
        const feedbackId = this.props.feedback?.feedback_id;

        if (!feedbackId) {
            return;
        }

        // Ensure any Bootstrap tooltip attached to the "Send Now" button is closed
        // before we potentially remove the button from the DOM.
        try {
            const $sendNow = jQuery('.ai-send-now');
            if ($sendNow && $sendNow.length) {
                $sendNow.tooltip('hide');
                $sendNow.tooltip('dispose');
            }
        } catch (e) { /* ignore */ }

        $.ajax({
            url: `/feedback/${feedbackId}/chat-ai-autoresponse/send-now`,
            method: "POST",
            success: () => {
                this.setState({
                    countdownStart: null,
                    scheduledAt: null,
                    promptOutput: "",
                    hideAiRecommendationBox: true,
                    ...(this.state.aiSettingsAutomationType === 'semi' ? {
                        ai_chat_stopped_at: new Date().toISOString(),
                        ai_chat_stop_reason: 'conversation_concluded'
                    } : {}),
                });

                notifySuccess(__('Message sent successfully!'));
            },
            error: (err) => {
                console.error("Failed to send AI auto response now", err);
                notifyError(__('Failed to send message. Please try again.'));
            },
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.props?.scheduledAt && this.state.initial_feedback !== this.props.feedback_id) {
            const saved_message_index = this.state.typing_message.findIndex((value) => {
                if (value.id === this.props.feedback_id) {
                    return true;
                } else {
                    return false;
                }
            });
            // Redux clears window.chatAttachments on feedback switch; keep chips in lockstep.
            this.revokeAttachmentUrls();
            window.chatAttachments = [];
            this.setState({
                value: saved_message_index === -1 ? "" : this.state.typing_message[saved_message_index].message,
                initial_feedback: this.props.feedback_id,
                cleared: true,
                attachments: [],
            });
            this.calcTextareaHeight();
        }

        if (this.props.feedbackPrompt === prevProps.feedbackPrompt && this.props.loading) {
            let element = document.getElementById('chat-textarea');
            if (element) {
                element.focus();
                this.calcTextareaHeight();
            }
        }

        if ((!this.props?.promptObj || !this.props?.scheduledAt) && this.props.feedbackPrompt !== prevProps.feedbackPrompt) {
            this.clearTimer()
        }

        if (this.state.value !== prevState.value) {
            let element = document.getElementById('chat-textarea');
            if (element) {
                element.focus();
                this.calcTextareaHeight();
            }
            this.saveToTypingMessage(this.state.value)
        }

        // If the countdown was visible and is now gone, make sure any tooltip
        // that was attached to the (now hidden) Send Now / Cancel buttons is
        // also cleaned up.
        if (prevState.countdownStart && !this.state.countdownStart) {
            try {
                jQuery('.ai-send-now, .ai-cancel-automation').tooltip('hide').tooltip('dispose');
            } catch (e) { /* ignore */ }
        }

        // AI Response
        const AIGenRecommendation = this.props?.feedback?.prompts[0]?.promptOutput;
        const AutomationType = this.props?.feedback?.aiSettingsAutomationType;
        const AIChatPromptStatus = this.props?.feedback?.prompts[0]?.status;
        const AIPromptOutput = this.props?.promptObj?.promptOutput;

        if (this.state.aiSettingsAutomationType !== AutomationType) {
            this.setState({ aiSettingsAutomationType: AutomationType });
        }

        if (this.state.ai_chat_status !== AIChatPromptStatus) {
            this.setState({ ai_chat_status: AIChatPromptStatus });
        }

        // set scheduled at
        if (prevProps.scheduledAt !== this.props?.scheduledAt && this.props?.scheduledAt?.length > 0) {
            const seconds = getSecondsUntil(this.props?.scheduledAt);
            this.setState({
                countdownStart: seconds,
                promptOutput: AIPromptOutput,
                scheduledAt: this.props?.scheduledAt,
                hideAiRecommendationBox: false,
            });
        }

        const prevFirstPrompt = prevProps?.feedback?.prompts?.[0];
        const currFirstPrompt = this.props?.feedback?.prompts?.[0];
        const wasPromptAvailable = prevFirstPrompt?.status === 'available' && prevFirstPrompt?.type === 'ai';
        const isPromptAvailable = currFirstPrompt?.status === 'available' && currFirstPrompt?.type === 'ai';

        if (wasPromptAvailable && !isPromptAvailable && !this.state.hideAiRecommendationBox) {
            this.setState({ hideAiRecommendationBox: true });
        }

        // Semi-automated mode: hide the AI recommendation box when the user has already responded or AI was stopped
        // Conditions (all required):
        // 1. Box is currently visible (not already hidden)
        // 2. Automation type is "semi" (semi-automated)
        // 3. Either: chat has started (message was sent) OR AI was stopped (ai_chat_stop_reason exists and has content)
        if (!this.state.hideAiRecommendationBox && this.state.aiSettingsAutomationType === 'semi' && this.props?.feedback?.chat_status !== 'Chat Not Started') {
            this.setState({ hideAiRecommendationBox: true });
        }

        if (!this.props?.scheduledAt && AIGenRecommendation !== this.state.promptOutput) {
            if (!AIGenRecommendation || AIGenRecommendation.length < 1) {
                if (this.state.isAiPreviewVisible !== false) {
                    this.setState({ isAiPreviewVisible: false });
                }
            } else {
                if (this.props.feedback.chat_status === 'Chat Not Started') {
                    this.setState({ isAiPreviewVisible: true });
                } else {
                    this.setState({ isAiPreviewVisible: false });
                }

                if (this.state.promptOutput !== AIGenRecommendation) {
                    this.setState({ promptOutput: AIGenRecommendation });
                }
            }
        }

        if (this.props.conversationState) {
            const prevStateValue = prevProps.conversationState?.state;
            const currStateValue = this.props.conversationState.state;
            if (currStateValue && prevStateValue !== currStateValue) {
                this.setState({
                    ai_chat_stopped_at: new Date().toISOString(),
                    ai_chat_stop_reason: currStateValue,
                });
            }
        }

        if (prevProps.feedback?.feedback_id !== this.props?.feedback?.feedback_id) {
            this.setState({
                ai_chat_stopped_at: this.props?.feedback?.ai_chat_stopped_at ?? '',
                ai_chat_stop_reason: this.props?.feedback?.ai_chat_stop_reason ?? null,
            });
        }
        this.initAutomationIndicatorTooltip();
    }

    UNSAFE_componentWillReceiveProps() {
        if (this.props.loading) {
            this.clearTimer()
        }
    }

    componentDidMount() {
        this.props.dispatch(setTimerInterval());

        setInterval(() => {
            this.setState({ timeSinceFeedBackStarted: this.handleTimeSinceFeedBackStarted() })
        }, 1000);
        if (document.getElementById('chat-textarea')) {
            this.calcTextareaHeight();
        }

        const firstPrompt = this.props?.feedback?.prompts?.[0];
        const isPromptAvailable = firstPrompt?.status === 'available' && firstPrompt?.type === 'ai';
        if (!isPromptAvailable && !this.state.hideAiRecommendationBox) {
            this.setState({ hideAiRecommendationBox: true });
        }

        // scheduled at handling
        if (this.props?.scheduledAt) {
            const seconds = getSecondsUntil(this.props?.scheduledAt);
            this.setState({
                scheduledAt: this.props?.scheduledAt,
                countdownStart: seconds,
                promptOutput: this.props?.feedback?.prompts[0]?.promptOutput,
                hideAiRecommendationBox: false
            });
        }

        //
        if (this.props?.feedback?.ai_chat_stopped_at) {
            this.setState({
                ai_chat_stopped_at: this.props?.feedback?.ai_chat_stopped_at,
                ai_chat_stop_reason: this.props?.feedback?.ai_chat_stop_reason ?? null
            });
        }
        this.initAutomationIndicatorTooltip();
    }

    componentWillUnmount() {
        if (this.automationIndicatorTriggerRef) {
            try {
                jQuery(this.automationIndicatorTriggerRef).tooltip('dispose');
            } catch (e) { /* ignore */ }
        }
        // Clean up any stray tooltips that might still be attached to the
        // Send Now / Cancel automation buttons.
        try {
            jQuery('.ai-send-now, .ai-cancel-automation').tooltip('hide').tooltip('dispose');
        } catch (e) { /* ignore */ }
        this.revokeAttachmentUrls();
        // fix Warning: Can't perform a React state update on an unmounted component
        this.setState = (state, callback) => {
            return;
        };
        clearInterval(this.interval);
    }

    render() {
        const hasMessageContent = (this.state.value || '').trim() !== '' || (window.chatAttachments && window.chatAttachments.length > 0);
        const cannotChat = !(this.props.feedback.is_include_contact && this.props.feedback.user.phone) || (this.props.feedback.is_whatsapp && this.props.feedback.chats.length === 1);
        // RTF-4374: admin may have revoked this user's ability to send chats.
        const canSendChat = this.canSendChat();
        const isSendDisabled = this.props.loading || cannotChat || !hasMessageContent || !canSendChat;

        let isSafari = false;

        if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
            isSafari = true;
        }

        // condition to show AI Button & Preview
        const showAIButton = Boolean(this.props.current_user && ((this.props.feedback.is_include_contact && this.props.feedback.user.phone)
        || (this.props.feedback.is_whatsapp && this.props.feedback.chats.length === 1)));

        const firstPrompt = this.props?.feedback?.prompts[0];
        const isPromptAvailable = firstPrompt?.status === 'available' && firstPrompt?.type === 'ai';
        const isNotHumanIntervention = !this.state.humanIntervention;
        const aiChatStopReason = this.state.ai_chat_stop_reason || this.props?.feedback?.ai_chat_stop_reason;

        const condA_hideAiRecommendationBox = !this.state.hideAiRecommendationBox;
        const condB_isPromptAndNotEscalation = isPromptAvailable && aiChatStopReason !== 'escalation' && aiChatStopReason !== 'conversation_concluded' && aiChatStopReason !== 'manual_stop' && aiChatStopReason !== 'human_intervention';
        const condC_noStopReason = !aiChatStopReason;
        const condInner = condB_isPromptAndNotEscalation || condC_noStopReason;
        const condFinal = condA_hideAiRecommendationBox && condInner;

        const hasConversationAutomation =
            this.state.aiSettingsAutomationType === 'full'
            || this.state.aiSettingsAutomationType === 'semi';


        const showPasteButton = (!!this.state.countdownStart && !this.state.humanIntervention) || (isNotHumanIntervention || isPromptAvailable);

        return (
            <div className="chat chat--fullheight h-100 d-flex flex-column" id="chatsection">
                <div className="mb-0 p-3 border-bottom">
                    <h5 id="chat" className="d-inline">Chat</h5>
                    {this.props.feedback.response_time && !this.props.feedback.getLastChatIfFeedbacker ? <span className="float-right text-muted" style={{fontSize: '0.64rem'}}>{__('Response time')}: {this.props.feedback.response_time}</span> : null}
                    {((this.props.feedback.chat_status === "Chat Not Started" && this.props.feedback.is_include_contact && this.props.feedback.user.phone && (this.props.feedback.is_whatsapp || this.props.feedback.is_text)) ?
                        <span className="chat-alert-message"><i className="fas fa-exclamation-triangle text-warning mr-1"></i>{__('Not yet responded to')}&nbsp;({this.state.timeSinceFeedBackStarted} {__('since submitted')})</span> : "")}
                    {((this.props.feedback.chat_status !== "Chat Not Started" && this.props.feedback.getLastChatIfFeedbacker && this.props.feedback.is_include_contact && this.props.feedback.user.phone && (this.props.feedback.is_whatsapp || this.props.feedback.is_text)) ?
                        <span className="chat-alert-message"><i className="fas fa-exclamation-triangle text-warning mr-1"></i>{__('New Chat not responded to')}&nbsp;({this.state.timeSinceFeedBackStarted} )</span> : "")}
                    {(!this.props.feedback.is_text && !this.props.feedback.is_whatsapp && this.props.feedback.user.phone) &&
                        <span className={this.props.feedback.otf_id ? 'float-right font-weight-bold' : "float-right"} style={{fontSize: '0.64rem'}}>( {__('This customer did not request a response via chat')}. {getRequestedVia(this.props.feedback)} )</span>
                    }
                </div>

                <div className="p-3 h-100 chat_widget" id="chat_widget">
                    {(this.props.current_user && (typeof this.props.feedback.include_user_details == "undefined" || this.props.feedback.include_user_details != false)) &&
                        this.props.feedback.chats.length > 0 ? <Chats /> : null
                    }
                    {(!this.props.current_user || !(typeof this.props.feedback.include_user_details == "undefined" || this.props.feedback.include_user_details == false)) &&
                        this.props.feedback.chats.length > 0 ? <Chats hidename={true} /> : null
                    }
                    <Typing />
                </div>

                {/* RTF-4374: show a notice when the user cannot send chats */}
                {!canSendChat &&
                    <div className="p-3 border-top">
                        <p className="text-muted font-italic mb-0">{__('Your ability to send chats has been disabled')}</p>
                    </div>
                }

                {canSendChat && this.props.current_user && <div className="text-right" id="feedback_attachment_div">
                    <ChatAttachmentPreview attachments={this.state.attachments} onRemove={this.handleRemoveAttachment} />
                    <input type="file" className="feedback_attachment" multiple name="feedback_attachment[]"
                        id={this.attachmentInputId}
                        onChange={this.handleAttachmentChange}
                        accept="image/png, image/jpeg, image/*, video/mp4, video/quicktime, video/H264, video/mpeg, video/webm, application/pdf" />
                </div>}

                {canSendChat && this.props.current_user && <div className='pt-3 border-top'>

                    {(this.state.aiSettingsAutomationType !== 'none' && (this.props?.scheduledAt?.length > 0 || (isNotHumanIntervention || isPromptAvailable))) && (
                        <div className='chat-ai-recommendation-wrapper px-2'>
                            {this.state.countdownStart > 0 && (
                                <>
                                    {!this.state.ai_chat_stopped_at && (
                                        <p className='d-flex flex-column justify-content-between align-items-start textarea-label'>
                                            <span style={{ lineHeight: "110%"}}>{ __('AI Generated Recommendation')}<br />{ __('(message will be automatically sent in')}{": "}
                                                <CountdownTimer
                                                    startSeconds={this.state.countdownStart}
                                                    onComplete={() => {
                                                        // UI clears immediately because the scheduled_at time has passed —
                                                        // the 10s cron will deliver the message regardless. The API call
                                                        // below is an optimization for instant delivery, not the primary
                                                        // delivery mechanism. Unlike handleSendNow() (user-initiated,
                                                        // needs success/error feedback), this is automatic.
                                                        this.setState({
                                                            countdownStart: null,
                                                            hideAiRecommendationBox: true,
                                                            promptOutput: "",
                                                            scheduledAt: null,
                                                            ...(this.state.aiSettingsAutomationType === 'semi' ? { ai_chat_stopped_at: new Date().toISOString() } : {}),
                                                        });

                                                        const feedbackId = this.props.feedback?.feedback_id;
                                                        if (feedbackId) {
                                                            $.ajax({
                                                                url: `/feedback/${feedbackId}/chat-ai-autoresponse/send-now`,
                                                                method: "POST",
                                                                error: (err) => {
                                                                    // Not user-facing: cron will deliver within 10s as fallback.
                                                                    // 404 = cron already sent it (expected race condition).
                                                                    console.warn("Countdown send-now deferred to cron", err.status);
                                                                },
                                                            });
                                                        }
                                                    }}
                                                />{")"}
                                            </span>
                                        </p>
                                    )}
                                </>
                            )}

                            {/* !this.state.humanIntervention */}
                            {/* as long as the prompt is avail, the box shoud show. */}
                            {condFinal && (
                                <div className='chat-ai-recommendation-text' style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                    <div className='d-flex flex-row align-items-center'>
                                        <TypeAnimation
                                            key={this.state?.promptOutput?.length}
                                            sequence={[
                                                // Same substring at the start will only be typed out once, initially
                                                this.state?.promptOutput,
                                            ]}
                                            wrapper="p"
                                            speed={100}
                                            style={{ fontSize: '0.8rem', display: 'inline-block' }}
                                            repeat={0}
                                            className="ai-suggestion-type"
                                        />
                                    </div>
                                    <button
                                        className='d-block mt-1 mb-0 ml-auto pl-2'
                                        style={{ border: 0, background: 'transparent', color: '#3775B7', fontSize: '0.8rem', lineHeight: '10px', padding: 0, outline: 'none' }}
                                        onClick={(e) => {
                                            // The Bootstrap tooltip gets stuck after click because focus stays on
                                            // the button and the button later unmounts before BS can dispose it.
                                            // Blur, then dispose the tooltip + remove any orphaned .tooltip nodes.
                                            const btn = e.currentTarget;
                                            try { $(btn).tooltip('dispose'); } catch (err) {}
                                            btn.blur();
                                            $('.tooltip').remove();
                                            this.pasteMessage(this.state.promptOutput, this.props.feedback?.prompts?.[0]?.id);
                                        }}
                                        data-toggle="tooltip"
                                        data-placement="bottom"
                                        data-title={__('Paste recommendation to the message box and cancel chat automation for this conversation')}
                                    >{__('Paste to Message')}</button>
                                </div>
                            )}

                            {(hasConversationAutomation || showPasteButton) && (
                                <div className='d-flex flex-row justify-content-between chat-ai-recommendation-buttons'>
                                    {/* Automation indicator */}
                                    {hasConversationAutomation && (() => {
                                        const isStopped = !!(this.state.ai_chat_stopped_at || this.props?.feedback?.ai_chat_stopped_at);
                                        const stopReason = this.state.ai_chat_stop_reason ?? this.props?.feedback?.ai_chat_stop_reason ?? null;
                                        const automationConfig = getAutomationIndicatorConfig(this.state.aiSettingsAutomationType, isStopped, stopReason);
                                        return (
                                        <div className={"d-flex flex-row align-items-center"}>
                                            <span
                                                ref={el => { this.automationIndicatorTriggerRef = el; }}
                                                className="d-flex flex-row align-items-center automation-indicator-trigger"
                                                tabIndex={0}
                                                role="img"
                                                aria-label={automationConfig.label}
                                            >
                                                <svg style={{
                                                    transform: "scale(0.6)"
                                                }}
                                                    width="20" height="26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.125 10.333V1L1 15.667h7.875V25L19 10.333h-7.875Z" fill="#3775B7" stroke="#3775B7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></svg>

                                                <span style={{
                                                    display: "block",
                                                    width: "4px",
                                                    height: "4px",
                                                    borderRadius: "100%",
                                                    marginTop: "-16px",
                                                    marginLeft: "-5px",
                                                    marginRight: "10px",
                                                    background: !isStopped ? "lightgreen" : "red"
                                                }}
                                                ></span>

                                                <span>{automationConfig.label}</span>
                                            </span>

                                            {(!this.state.ai_chat_stopped_at || isPromptAvailable) && (
                                                <div className="d-flex flex-row justify-content-center align-items-center ml-2 ai-cancel-btn-wrapper" data-hide={!!this.state.ai_chat_stopped_at}>
                                                    {this.state.countdownStart > 0 && (
                                                        <>
                                                            <span style={{ padding: '0 1.5px' }}>|</span>
                                                            <button
                                                                className="d-block mt-0 mb-0 ai-send-now"
                                                                style={{ border: 0, background: 'transparent', color: '#1C75BC' }}
                                                                onClick={this.handleSendNow.bind(this)}
                                                                data-toggle="tooltip"
                                                                data-placement="bottom"
                                                                data-title={__('Send the automated message now without waiting for the countdown to complete')}
                                                            >
                                                                {__('Send Now')}
                                                            </button>
                                                        </>
                                                    )}
                                                    <span style={{ padding: '0 1.5px' }}>|</span>
                                                    <button
                                                        className="d-block mt-0 mb-0 ai-cancel-automation"
                                                        style={{ border: 0, background: 'transparent', color: '#FF5858' }}
                                                        onClick={this.handleCancelAutomation.bind(this)}
                                                        data-toggle="tooltip"
                                                        data-placement="bottom"
                                                        data-title={__('Cancel future chat automation for this conversation')}
                                                    >
                                                        {__('Cancel')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })()}

                                </div>
                            )}
                        </div>
                    )}


                    {/* AI Generated Recommendation Preview */}
                    {this.state.aiSettingsAutomationType === "none" && this.state.promptOutput && this.state.isAiPreviewVisible && this.props.feedback.chat_status === 'Chat Not Started' && showAIButton && this.state.ai_chat_status !== "sent" &&
                        <div className='chat-ai-recommendation-wrapper px-2' data-visible={this.state.isAiPreviewVisible}>
                            {!this.state.hideAiRecommendationBox && (
                                <p className='d-flex flex-column justify-content-between align-items-start textarea-label'>
                                    <span style={{ lineHeight: "110%"}}>{ __('AI Generated Recommendation')}</span>
                                </p>
                            )}

                            <div className='chat-ai-recommendation-text'>
                                <TypeAnimation
                                    key={this.state?.promptOutput?.length}
                                    sequence={[
                                        // Same substring at the start will only be typed out once, initially
                                        this.state?.promptOutput,
                                    ]}
                                    wrapper="p"
                                    speed={100}
                                    style={{ fontSize: '0.8rem', display: 'inline-block' }}
                                    repeat={0}
                                    className="ai-suggestion-type"
                                />
                            </div>

                            <div className='d-flex flex-row justify-content-end chat-ai-recommendation-buttons'>
                                <div className={"d-flex flex-row justify-content-end"}>
                                    <button className='mt-0 mb-0 mr-0 ml-auto pl-2 btn-text' onClick={this.pasteMessage.bind(this, this.state.promptOutput, this.props.feedback?.prompts?.[0]?.id)}>{__('Paste to Message')}</button>
                                </div>
                            </div>
                        </div>
                    }

                    <div className="px-2 mt-0 w-100 d-flex flex-column align-items-center pb-2">
                        {/* Chat input — attachment icon inside left, send button inside right */}
                        <div className='w-100' style={{ position: 'relative' }}>
                            <label htmlFor={this.attachmentInputId} id="feedback_attachment_label" className="mb-0"
                                style={{ position: 'absolute', left: '12px', bottom: '12px', zIndex: 2, cursor: 'pointer', lineHeight: 0 }}>
                                <img className="notes-icon" src="/img/chat-attachment.svg" data-toggle="tooltip" data-placement="bottom" data-title={__('Attach file(s) to your message')} />
                            </label>
                            <textarea
                                onChange={(e) => {
                                    this.handleChange(e)
                                }}
                                onKeyUp={this.handleChatType.bind(this)}
                                onClick={this.handleTextClick.bind(this)}
                                id="chat-textarea"
                                value={this.state.value}
                                style={{
                                    resize: "none",
                                    minHeight: "50px",
                                    maxHeight: "none",
                                    paddingLeft: "44px",
                                    paddingRight: "54px",
                                }}
                                className="chat-dialog-textarea"
                                placeholder={(this.props.feedback.is_whatsapp && this.props.feedback.chats.length === 1) ? __("Whatsapp's regulations require the user to respond before another message can be sent.") : __("Type your message") + '...'}
                                aria-label={__("Type your message") + '...'}
                                readOnly={((!(this.props.feedback.is_include_contact && this.props.feedback.user.phone) || (this.props.feedback.is_whatsapp && this.props.feedback.chats.length === 1)) ? "readOnly" : false)}
                            />
                            <button className={"btn btn-sm p-2 " + (isSendDisabled ? "disabled btn-secondary" : "btn-primary")}
                                    disabled={isSendDisabled}
                                    onClick={this.handleSend.bind(this)}
                                    type="button"
                                    style={{ position: 'absolute', right: '8px', bottom: '8px', zIndex: 2 }}>
                                <img src="/img/send-white.svg" />
                            </button>
                        </div>

                        {(this.state.aiSettingsAutomationType === "full" && !!this.state.value.length && !this.state.ai_chat_stopped_at.length) && (
                            <div className='d-flex flex-row justify-content-between mt-2'>
                                <span>
                                    <i className="fas fa-exclamation-triangle text-warning mr-1"></i>
                                    { __('Note: sending a manual chat message will cancel future AI chat automation for this conversation.') }
                                </span>
                            </div>
                        )}

                    </div>

                    {/* Buttons Wrapper — moved under the textarea */}
                    <div className='chat-buttons-wrapper px-2'>
                        {/* AI Button — RTF-4904: hidden entirely once the chat has started
                            (it was only usable for the initial response), rather than shown
                            in a disabled state. The transient loading-disabled state still
                            renders while chat_status is "Chat Not Started". */}
                        {this.state.aiSettingsAutomationType === "none" && !this.state.isAiPreviewVisible && showAIButton && this.props.feedback.chat_status === 'Chat Not Started'
                            ? <AiButton location={'aiButton'} handleTick={this.handleTick} seconds={this.state.seconds} />
                            : null
                        }

                        {/* Insert Rating & Canned Responses */}
                        {this.props.current_user && <ChatSectionButtons handleReview={this.handleReview.bind(this)} handleReviewOnly={this.handleReviewOnly.bind(this)} handlePreCanned={this.handlePreCanned.bind(this)} />}

                        {/* Request GPS */}
                        {(this.props.feedback.allow_gps_feedback && this.props.current_user && ((this.props.feedback.is_include_contact && this.props.feedback.user.phone)
                            || (this.props.feedback.is_whatsapp && this.props.feedback.chats.length === 1)))
                            ? <div className="d-flex justify-content-center btn-spacer">
                                <button
                                    className="btn btn-md btn-primary btn-primary-alt no-arrow text-nowrap btn-chat"
                                    data-text={__('Can you share your current GPS location so we can better assist you? If so, please click the link below to allow a one-time sharing of your current location') + ':\nhttps://' + this.props.feedback.friendly_access_url + '/gps/' + this.props.feedback.short_id}
                                    onClick={this.handleGpsRequest.bind(this)}
                                    type="button"
                                >
                                    <svg className='ml-1 mr-1' width="11" height="11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clipPath="url(#a)">
                                            <path d="M9.625 5.5A4.125 4.125 0 0 1 5.5 9.625M9.625 5.5A4.125 4.125 0 0 0 5.5 1.375M9.625 5.5h-8.25M5.5 9.625A4.125 4.125 0 0 1 1.375 5.5M5.5 9.625c.76 0 1.375-1.847 1.375-4.125S6.259 1.375 5.5 1.375m0 8.25c-.76 0-1.375-1.847-1.375-4.125S4.741 1.375 5.5 1.375m0 0A4.125 4.125 0 0 0 1.375 5.5"
                                            stroke="#3775B7"
                                            strokeWidth=".917"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"/>
                                        </g>
                                        <defs>
                                            <clipPath id="a">
                                                <path fill="#fff" d="M0 0h11v11H0z"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    {__('Request GPS')}
                                </button>
                            </div>
                            : null
                        }
                    </div>

                    {/* RTF-4809/4898: Feedback Insights — under the action buttons, with a separator.
                        Only rendered when the AI detected at least one industry category for this
                        feedback (so the separator + box stay absent otherwise). */}
                    {this.props.feedback?.industryCategoryMatches?.length > 0 && (
                        <div className='px-2 pt-3 mt-2' style={{ borderTop: '1px solid #e1e2e2' }}>
                            <AiReasoningReport matches={this.props.feedback.industryCategoryMatches} guideBook={this.props.feedback.industryAiGuideBook} />
                        </div>
                    )}
                </div>}

                {/* RTF-4739: guest visitors (e.g. /forward/<token> recipients who are not logged in)
                    should see an explicit prompt instead of empty chat infra. */}
                {!this.props.current_user &&
                    <div className="p-3 border-top">
                        <p className="text-muted font-italic mb-0">
                            {__('To send a chat you must be logged in.')}{' '}
                            <a href="/login">{__('Log in here.')}</a>
                        </p>
                    </div>
                }

                <div className="mt-1 mb-1">
                    <Alert alert={this.props.alert} location={'chat'}  />
                </div>

                <Toaster
                    position="top-center"
                    toastOptions={{
                        duration: 3000,
                    }}
                />

            </div>

        );
    }
}

function mapStateToProps(state) {
    return {
        feedback: state.feedback_detail,
        loading: state.loading,
        loading_location: state.loading_location,
        loading_data: state.loading_data,
        feedback_id: state.selected_feedback,
        alert: state.alert,
        feedbackPrompt: state.feedbackPrompt,
        current_user: state.current_user,
        chatAnyway: state.chatAnyway,
        scheduledAt: state.scheduledAt,
        promptObj: state.promptObj,
        conversationState: state.conversationState,
    }
}
export default connect(mapStateToProps)(ChatSection);
